import React, { useState, useRef, useEffect } from 'react';
import { CometChat } from '@cometchat/chat-sdk-javascript';
import { CometChatButton } from '@cometchat/chat-uikit-react';
import '../../styles/CometChatUserProfile/CometChatUserProfile.css';

// API Configuration
//const API_BASE = 'http://localhost/cometchat-backend';
const API_BASE = 'https://neitclem-connect.gratusinc.org/cometchat-backend';

// Type definitions
interface ApiResponse {
  success: boolean;
  message?: string;
  user?: {
    uid: string;
    name: string;
    email: string;
    avatar: string;
  };
  error?: string;
}

interface UploadResponse {
  success: boolean;
  message?: string;
  url?: string;
  filename?: string;
  error?: string;
}

interface CometChatUserResponse {
  data: {
    uid: string;
    name: string;
    avatar: string;
    status: string;
    metadata?: any;
  };
}

interface UserProfileEditProps {
  user: CometChat.User;
  onHide: () => void;
  onUserUpdated: (user: CometChat.User) => void;
}

const CometChatUserProfile: React.FC<UserProfileEditProps> = ({
  user,
  onHide,
  onUserUpdated
}) => {
  const [name, setName] = useState(user.getName() || '');
  const [avatar, setAvatar] = useState(user.getAvatar() || '');
  const [isUploading, setIsUploading] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState('');
  const [previewUrl, setPreviewUrl] = useState(user.getAvatar() || '');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Fetch latest avatar when component mounts
  useEffect(() => {
    const refreshUserAvatar = async () => {
      const latestUser = await fetchUserFromCometChat(user.getUid());
      if (latestUser && latestUser.getAvatar()) {
        setPreviewUrl(latestUser.getAvatar());
        setAvatar(latestUser.getAvatar());
      }
    };
    
    refreshUserAvatar();
  }, [user]);

  // Function to fetch latest user data from CometChat REST API
  const fetchUserFromCometChat = async (uid: string): Promise<CometChat.User | null> => {
    try {
      // You'll need to add your CometChat credentials to your config.php or create a new endpoint
      const response = await fetch(`${API_BASE}/get_cometchat_user.php?uid=${uid}`);
      
      if (!response.ok) {
        return null;
      }

      const userData = await response.json();
      
      if (userData.success && userData.data) {
        const cometChatUser = new CometChat.User(userData.data.uid);
        cometChatUser.setName(userData.data.name);
        cometChatUser.setAvatar(userData.data.avatar);
        return cometChatUser;
      }
      
      return null;
    } catch (error) {
      return null;
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleAvatarUpload(file);
    }
  };

  const handleAvatarUpload = async (file: File) => {
    setIsUploading(true);
    setError('');

    try {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        throw new Error('Please select an image file');
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        throw new Error('File size must be less than 5MB');
      }

      // Create FormData for upload
      const formData = new FormData();
      formData.append('file', file);

      // Upload to your PHP backend
      const uploadResponse = await fetch(`${API_BASE}/upload-avatar.php`, {
        method: 'POST',
        body: formData,
      });

      if (!uploadResponse.ok) {
        const errorText = await uploadResponse.text();
        throw new Error(`Failed to upload image: ${uploadResponse.status}`);
      }

      const responseText = await uploadResponse.text();
      
      let uploadData: UploadResponse;
      try {
        uploadData = JSON.parse(responseText) as UploadResponse;
      } catch (jsonError) {
        throw new Error('Server returned invalid response. Check console for details.');
      }
      if (!uploadData.success) {
        throw new Error(uploadData.error || 'Failed to upload image');
      }
      
      if (!uploadData.url) {
        throw new Error('No URL returned from upload');
      }
      
      const newAvatarUrl = uploadData.url;
      setAvatar(newAvatarUrl);
      setPreviewUrl(newAvatarUrl);

      // Give CometChat a moment to process, then refresh from their API
      setTimeout(async () => {
        const latestUser = await fetchUserFromCometChat(user.getUid());
        if (latestUser && latestUser.getAvatar()) {
          setPreviewUrl(latestUser.getAvatar());
          setAvatar(latestUser.getAvatar());
        }
      }, 1000); // Wait 1 second for CometChat to process
    } catch (err: any) {
      setError(err.message || 'Failed to upload avatar');
    } finally {
      setIsUploading(false);
    }
  };

  const handleSave = async () => {
    if (!name.trim()) {
      setError('Name is required');
      return;
    }

    setIsUpdating(true);
    setError('');

    try {
      // Update user via your PHP backend
      const response = await fetch(`${API_BASE}/update_user.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          uid: user.getUid(),
          name: name.trim(),
          avatar: avatar,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to update profile: ${response.status}`);
      }

      const responseText = await response.text();
      
      let data: ApiResponse;
      try {
        data = JSON.parse(responseText) as ApiResponse;
      } catch (jsonError) {
        throw new Error('Server returned invalid response. Check console for details.');
      }
      
      if (data.success && data.user) {
        // Fetch the latest user data from CometChat to get the updated avatar
        const updatedCometChatUser = await fetchUserFromCometChat(user.getUid());
        
        if (updatedCometChatUser) {
          // Use the data from CometChat (which should have the latest avatar)
          onUserUpdated(updatedCometChatUser);
        } else {
          // Fallback to our backend data if CometChat fetch fails
          const fallbackUser = new CometChat.User(user.getUid());
          fallbackUser.setName(data.user.name);
          fallbackUser.setAvatar(data.user.avatar);
          onUserUpdated(fallbackUser);
        }
        
        // Close the modal
        onHide();
      } else {
        throw new Error(data.error || 'Failed to update profile');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to update profile');
    } finally {
      setIsUpdating(false);
    }
  };

  const triggerFileUpload = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="user-profile-edit__wrapper">
      <div className="user-profile-edit">
        <div className="user-profile-edit__header">
          <div className="user-profile-edit__header-title">Edit Profile</div>
          <div className="user-profile-edit__header-close" onClick={onHide}></div>
        </div>

        <div className="user-profile-edit__content">
          <div className="user-profile-edit__avatar-section">
            <div className="user-profile-edit__avatar-container">
              {previewUrl ? (
                <img
                  src={previewUrl}
                  alt="User Avatar"
                  className="user-profile-edit__avatar-image"
                />
              ) : (
                <div className="user-profile-edit__avatar-placeholder">
                  {name.charAt(0).toUpperCase() || 'U'}
                </div>
              )}
              <div className="user-profile-edit__avatar-overlay" onClick={triggerFileUpload}>
                {isUploading ? (
                  <div className="user-profile-edit__loading">Uploading...</div>
                ) : (
                  <div className="user-profile-edit__camera-icon">📷</div>
                )}
              </div>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={handleFileSelect}
            />
            <div className="user-profile-edit__avatar-hint">
              Click to change avatar
            </div>
          </div>

          <div className="user-profile-edit__form">
            <div className="user-profile-edit__input-group">
              <label className="user-profile-edit__label">Name</label>
              <input
                type="text"
                className="user-profile-edit__input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter your name"
                disabled={isUpdating}
              />
            </div>

            <div className="user-profile-edit__input-group">
              <label className="user-profile-edit__label">Email</label>
              <input
                type="email"
                className="user-profile-edit__input user-profile-edit__input--disabled"
                value={user.getUid().includes('@') ? user.getUid() : 'Not available'}
                disabled
              />
            </div>

            <div className="user-profile-edit__input-group">
              <label className="user-profile-edit__label">User ID</label>
              <input
                type="text"
                className="user-profile-edit__input user-profile-edit__input--disabled"
                value={user.getUid()}
                disabled
              />
            </div>
          </div>

          {error && (
            <div className="user-profile-edit__error">
              {error}
            </div>
          )}

          <div className="user-profile-edit__actions">
            <div className="user-profile-edit__cancel-wrapper">
              <CometChatButton
                text="Cancel"
                onClick={onHide}
                disabled={isUpdating}
              />
            </div>
            <div className="user-profile-edit__save-wrapper">
              <CometChatButton
                text={isUpdating ? 'Saving...' : 'Save Changes'}
                onClick={handleSave}
                disabled={isUpdating || isUploading}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CometChatUserProfile;